"use client";

import { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { usePathname } from "@/lib/spa/navigation";

/**
 * Paths that must stay reachable when the password has expired.
 *
 * The change screen itself, obviously, and the routes it and the login page
 * need. Without `/api/auth/*` the user would be redirected to a form that
 * cannot submit — a lockout built out of a policy.
 *
 * Lives HERE rather than in the root shell so the shell can stay
 * synchronous. An await in the root layout used to delay every nested
 * loading state and every client navigation — including, on SQLite then,
 * a `busy_timeout` of 30s if a writer held the lock. This component is a
 * Suspense sibling of the page slot: the chrome and the destination render
 * without waiting on it.
 */
export const ROTATION_EXEMPT = ["/account/password", "/login", "/features", "/api/auth"];

/**
 * Credential freshness + rotation. The HMAC cookie gate already verified the
 * signature; this is the database half (stale session / must-rotate), fetched
 * from `/api/auth/me` so the SPA shell never blocks on the database.
 *
 * FAIL OPEN on an unknown path. A gate that redirects when it cannot tell
 * where it is has no way to exempt its own destination, which is exactly how
 * this shipped a redirect loop: /login came through without the header, the
 * gate saw a stale cookie and sent the browser to /login again.
 */
export function SessionGate() {
  const pathname = usePathname();
  const navigate = useNavigate();

  useEffect(() => {
    const known = pathname.startsWith("/");
    if (!known) return;
    if (ROTATION_EXEMPT.some((p) => pathname.startsWith(p))) return;
    let cancelled = false;
    void fetch("/api/auth/me", { credentials: "same-origin" })
      .then(async (res) => {
        if (!res.ok || cancelled) return;
        const status = (await res.json()) as { state?: string };
        if (cancelled) return;
        if (status.state === "stale") void navigate({ to: "/login" as never, replace: true });
        if (status.state === "must-rotate") void navigate({ to: "/account/password" as never, replace: true });
      })
      .catch(() => {
        /* fail open — an unreachable API is an outage, not a logout */
      });
    return () => {
      cancelled = true;
    };
  }, [pathname, navigate]);

  return null;
}
